import {
  ActionDropdown,
  ActionDropdownContent,
  ActionDropdownTrigger,
} from "@/components/ActionDropdown";
import { DocumentType } from "@/services/document.service";
import { ColumnDef } from "@tanstack/react-table";
import { FileText, MoreHorizontal, Trash2 } from "lucide-react";
import { format } from "date-fns";

export type DocumentActionType = {
  action: "delete";
  id: string;
};

// Formats document types automatically from "id_proof" -> "ID Proof"
const formatDocType = (type: string) => {
  if (type === "id_proof") return "ID Proof";
  if (type === "noc") return "NOC";
  if (type === "marksheet") return "Marksheet";
  if (type === "certificate") return "Certificate";
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const createDocumentColumns = (
  onActionClick: (payload: DocumentActionType) => void
): ColumnDef<DocumentType>[] => [
  {
    accessorKey: "document_type",
    header: "Document Type",
    cell: ({ row }) => (
      <span className="font-semibold text-gray-800">
        {formatDocType(row.original.document_type)}
      </span>
    ),
  },
  {
    accessorKey: "file_url",
    header: "Viewing Link",
    cell: ({ row }) => (
      <a 
        href={row.original.file_url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium max-w-[200px] truncate"
      >
        <FileText size={16} className="shrink-0" />
        <span className="truncate">Open File</span>
      </a>
    ),
  },
  {
    accessorKey: "verification_status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.verification_status?.toLowerCase() || "pending";
      let colorClass = "bg-gray-100 text-gray-700 border-gray-200";

      if (status === "verified") {
        colorClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
      } else if (status === "pending") {
        colorClass = "bg-amber-50 text-amber-700 border-amber-200";
      } else if (status === "rejected") {
        colorClass = "bg-red-50 text-red-700 border-red-200";
      }

      return (
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${colorClass} capitalize`}>
          {status}
        </span>
      );
    },
  },
  {
    accessorKey: "uploaded_at",
    header: "Uploaded On",
    cell: ({ row }) => {
      const date = row.original.uploaded_at;
      return date ? format(new Date(date), "dd MMM yyyy") : "-";
    },
  },
  {
    id: "actions",
    header: "",
    cell: ({ row }) => {
      const doc = row.original;

      return (
        <ActionDropdown>
          <ActionDropdownTrigger
            triggerContent={<MoreHorizontal className="h-4 w-4" />}
            buttonVariant="ghost"
            ariaLabel="Open menu"
            className={{ buttonClass: "h-8 w-8 p-0 float-right" }}
          />
          <ActionDropdownContent
            align="end"
            dropdownShape={[
              {
                type: "item",
                label: "Delete Document",
                icon: <Trash2 size={16} />,
                className: "cursor-pointer gap-2 text-red-600 focus:text-red-700",
                onSelect: () => onActionClick({ action: "delete", id: doc.id }),
              },
            ]}
          />
        </ActionDropdown>
      );
    },
  },
];
